import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/app/AuthContext';
import { Icon } from '@/components/Icon';
import {
  downloadExport,
  type ExportFormat,
  type ExportParams,
  type ExportReport,
} from '@/services/exports.service';
import { ApiRequestError } from '@/services/api';
import { Permission } from '@/types/permission';
import { v } from '@/theme/tokens';

interface ExportMenuProps {
  report: ExportReport;
  params: ExportParams;
}

/**
 * PDF / Excel export dropdown. Server-side permission (reports:export) is the
 * real gate — this menu merely hides itself for roles without it.
 */
export function ExportMenu({ report, params }: ExportMenuProps) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  if (!can(Permission.EXPORT_REPORTS)) return null;

  const run = async (format: ExportFormat) => {
    setBusy(format);
    setError(null);
    setOpen(false);
    try {
      await downloadExport(report, format, params);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Экспорт не удался');
    } finally {
      setBusy(null);
    }
  };

  const item = (format: ExportFormat, label: string) => (
    <button
      type="button"
      role="menuitem"
      className="rt-menu__item"
      onClick={() => void run(format)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 36,
        padding: '8px 12px',
        fontSize: 13,
      }}
    >
      <Icon name="doc" size={15} color={v('ink-muted')} />
      {label}
    </button>
  );

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="rt-btn rt-btn--ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        // In flight is not unavailable: the button keeps full strength and
        // says what it is doing, while a second click is still refused.
        aria-busy={busy !== null}
        disabled={busy !== null}
        onClick={() => setOpen((current) => !current)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          minHeight: 36,
          padding: '8px 14px',
          fontWeight: 600,
          fontSize: 13.5,
          cursor: busy ? 'progress' : 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <Icon name="doc" size={15} color={v('ink-muted')} />
        {busy ? `Экспорт ${busy.toUpperCase()}…` : 'Экспорт'}
        <Icon name="chevron" size={13} color={v('ink-muted')} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Экспорт"
          className="rt-menu rt-menu--end"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 50,
            minWidth: 150,
            padding: 4,
          }}
        >
          {item('pdf', 'PDF')}
          {item('xlsx', 'Excel')}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 50,
            width: 240,
            background: v('negative-soft'),
            color: v('negative'),
            borderRadius: 10,
            padding: '8px 10px',
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
